// The card's section, as the API's rules.cardStatus decides it (sbly-dash-collector
// src/intelligence/baselines/rules.ts): mirrored here only to re-read a card once some of its
// flags are explained (annotated). Keep the two in step.
import { OverviewCard } from './types';

type Flag = OverviewCard['flagged'][number];
const POLICIES = {
  tracking: { minWorseOut: 1, singleWorseOutIsNear: false, minWorseNear: 1, profitNearIsNear: false, contextCounts: false, edgeOnlyCounts: false },
  campaign: { minWorseOut: 2, singleWorseOutIsNear: true, minWorseNear: 2, profitNearIsNear: true, contextCounts: true, edgeOnlyCounts: true },
} as const;

export function cardStatusOf(c: OverviewCard, flags: Flag[]): OverviewCard['status'] {
  const policy = POLICIES[c.policy ?? (c.grain === 'pipeline' ? 'tracking' : 'campaign')];
  const against = flags.filter(f => f.verdict === 'worse' && (f.thinData || ((policy.contextCounts || !f.context) && (policy.edgeOnlyCounts || !f.edgeOnly))));
  const onlyMinor = against.length > 0 && against.every(f => f.minor);
  const worseOut = onlyMinor ? 0 : against.filter(f => f.status === 'out').length;
  const worseNear = onlyMinor ? 0 : against.filter(f => f.status === 'near').length;
  const profitNear = policy.profitNearIsNear && against.some(f => f.status === 'near' && f.profit);
  const better = flags.filter(f => f.verdict === 'better');
  const weight = (f: Flag) => (f.status === 'out' ? 2 : 1);
  const impact = c.impact.perDay;
  const doingBetter = impact != null && impact > 0 && !against.some(f => f.profit) && better.reduce((n, f) => n + weight(f), 0) > against.reduce((n, f) => n + weight(f), 0);
  const read = c.summary.out + c.summary.near + c.summary.in;
  const mostlyUnread = read === 0 || (c.summary.insufficient > (read + c.summary.insufficient) / 2 && !flags.some(f => f.thinData));
  const stalled = !!c.stalled;
  if (c.missing.length > 0 && !stalled) return 'missing';
  if (mostlyUnread && !stalled && !c.losing) return 'insufficient';
  if (c.losing && worseOut < policy.minWorseOut) return 'near';
  if (doingBetter && (worseOut > 0 || worseNear > 0)) return 'better';
  if (worseOut >= policy.minWorseOut) return 'out';
  if ((policy.singleWorseOutIsNear && worseOut === 1) || worseNear >= policy.minWorseNear || profitNear || stalled) return 'near';
  if (better.length > 0 && worseOut === 0 && worseNear === 0) return 'over';
  return read > 0 || flags.length > 0 ? 'in' : 'insufficient';
}
